import {
  UPDATE_APPLE_PAY_AVAILABLE,
  UPDATE_GOOGLE_PAY_AVAILABLE,
  API_CALL,
  API_CALL_SUCCESS,
  TOGGLE_SHOW_MARGIN_IN_SPOOF_MODE,
  TOGGLE_SHOW_SPOOF_LAUNCHPAD,
  DISMISS_LUX_PLUS_FREE_PREVIEW_BANNER,
  HOTEL_CANCELLATION_PRICE_CHANGE_MODAL_SEEN,
  DISMISS_BETA_TRIP_BANNER,
  SET_HEADLESS_MODE,
  ADD_HISTORICAL_ACTION,
  CLEAR_HISTORICAL_ACTIONS,
  RESTORE_HISTORICAL_ACTIONS,
} from './actionConstants'
import { FETCH_MERCHANT_FEE_CONFIG, FETCH_PAYTO_SUPPORTED_BANKS, FETCH_DEPOSIT_CONFIG, FETCH_PAYPAL_SETTINGS, FETCH_LAB_FEATURE_FLAG_DECISIONS, FETCH_LAB_REMOTE_CONFIG, FETCH_LAB_HEADER_BANNERS, FETCH_LAB_MODAL_POPUPS } from './apiActionConstants'
import { getMerchantFeeConfig, getPayToSupportedBanks, getDepositConfig, getPayPalSettings } from 'api/payment'
import { setDismissed as cookieDismiss } from 'luxPlus/cookies/freePreviewBanner'
import { setMarginInSpoofModeCookie } from 'cookies/showMarginInSpoofMode'
import { setShowSpoofLaunchpadCookie } from 'cookies/showSpoofLaunchpad'
import type { AppAction } from './types/ActionTypes'
import { betaAiTripBannerDismissed } from 'tripPlanner/cookies/betaAiTripBanner'
import { getFeatureFlagDecisions, getHeaderBanners, getModalPopups, getRemoteConfigFlags, FUNNEL_OPTIMISER_API_TIMEOUT_MS } from 'api/lab'
import { LabFeatureFlags } from 'constants/lab'
import { reportClientError } from 'services/errorReportingService'
import { extractFunnelFeatures, DEFAULT_FUNNEL_FEATURES } from 'checkout/selectors/funnel'
import { LE_LIVE_UTM_MEDIUM } from 'constants/payment'
import { getLabGeoAttributes, getLabModalPopupsRequestKey } from 'lib/lab/labRequestContext'
import config from 'constants/config'

export function setHeadlessMode(headlessMode: boolean): AppAction {
  return {
    type: SET_HEADLESS_MODE,
    headlessMode,
  }
}

export function updateCanPayWithApplePay(flag: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.system.canPayWithApplePay === flag) {
      // value won't change, don't need to send an action
      return
    }

    dispatch({
      type: UPDATE_APPLE_PAY_AVAILABLE,
      value: flag,
    })
  }
}

export function updateCanPayWithGooglePay(flag: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.system.canPayWithGooglePay === flag) {
      // value won't change, don't need to send an action
      return
    }

    dispatch({
      type: UPDATE_GOOGLE_PAY_AVAILABLE,
      value: flag,
    })
  }
}

export const DEFAULT_MERCHANT_FEE_CONFIG_CACHE_KEY = 'default'

export function getMerchantFeeConfigCacheKey(utmMedium: string): string {
  return utmMedium?.toLowerCase() === LE_LIVE_UTM_MEDIUM ? `utm_medium:${LE_LIVE_UTM_MEDIUM}` : DEFAULT_MERCHANT_FEE_CONFIG_CACHE_KEY
}

export function fetchMerchantFeeConfig(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const utmMedium = state.utm.medium
    const cacheKey = getMerchantFeeConfigCacheKey(utmMedium)
    if (state.system.merchantFeeConfigs.length !== 0 && state.system.merchantFeeConfigCacheKey === cacheKey) return
    dispatch({
      type: API_CALL,
      api: FETCH_MERCHANT_FEE_CONFIG,
      cacheKey,
      request: () => getMerchantFeeConfig(utmMedium),
    })
  }
}

export function fetchPayToSupportedBanks(): AppAction {
  return (dispatch, getState) => {
    const { system } = getState()
    if (system.payToSupportedBanks.length !== 0) return

    dispatch({
      type: API_CALL,
      api: FETCH_PAYTO_SUPPORTED_BANKS,
      request: () => getPayToSupportedBanks(),
    })
  }
}

export function fetchDepositConfig(): AppAction {
  return (dispatch, getState) => {
    const { system } = getState()
    if (system.depositConfigs) return

    dispatch({
      type: API_CALL,
      api: FETCH_DEPOSIT_CONFIG,
      request: () => getDepositConfig(),
    })
  }
}

export function fetchPayPalSettings(): AppAction {
  return (dispatch, getState) => {
    const { system } = getState()
    if (system.payPalSettings) return

    dispatch({
      type: API_CALL,
      api: FETCH_PAYPAL_SETTINGS,
      request: () => getPayPalSettings(),
    })
  }
}

export function toggleShowMarginInSpoofMode(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const showMarginInSpoofMode = state.system.showMarginInSpoofMode
    setMarginInSpoofModeCookie(!showMarginInSpoofMode)
    dispatch({
      type: TOGGLE_SHOW_MARGIN_IN_SPOOF_MODE,
      showMarginInSpoofMode: !showMarginInSpoofMode,
    })
  }
}

export function toggleShowSpoofLaunchpad(): AppAction {
  return (dispatch, getState) => {
    const showSpoofLaunchpad = getState().system.showSpoofLaunchpad
    setShowSpoofLaunchpadCookie(!showSpoofLaunchpad)
    dispatch({
      type: TOGGLE_SHOW_SPOOF_LAUNCHPAD,
      showSpoofLaunchpad: !showSpoofLaunchpad,
    })
  }
}

export function dismissFreePreviewBanner(): AppAction {
  cookieDismiss()
  return {
    type: DISMISS_LUX_PLUS_FREE_PREVIEW_BANNER,
  }
}

export function dismissBetaTripBanner(): AppAction {
  betaAiTripBannerDismissed()
  return {
    type: DISMISS_BETA_TRIP_BANNER,
  }
}

export function hotelCancellationPriceChangeModalSeen(): AppAction {
  return {
    type: HOTEL_CANCELLATION_PRICE_CHANGE_MODAL_SEEN,
  }
}

export function fetchLabFeatureFlagDecisions(keys: Array<string>, distinctId: string, forceRefresh?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const featureFlagContext = state.system.features.context
    const labGeoAttributes = getLabGeoAttributes(state)

    dispatch({
      type: API_CALL,
      api: FETCH_LAB_FEATURE_FLAG_DECISIONS,
      request: () => getFeatureFlagDecisions({
        keys,
        distinctId,
        featureFlagContext,
        forceRefresh,
        additionalAttributes: labGeoAttributes,
      }),
    })
  }
}

export function fetchLabRemoteConfig(keys: Array<string>, forceRefresh?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const keysToFetch = forceRefresh
      ? keys
      : keys.filter(key => state.system.features.remoteConfigFlags[key] === undefined)

    if (keysToFetch.length === 0) return

    dispatch({
      type: API_CALL,
      api: FETCH_LAB_REMOTE_CONFIG,
      request: () => getRemoteConfigFlags(keysToFetch),
    })
  }
}

function getLabRemoteConfigKeysForCurrentState() {
  return config.LAB_REMOTE_CONFIG_KEYS ?? []
}

export function refreshLabFeatureFlagsForCurrentUser(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const labFeatureFlags = config.LAB_FEATURE_FLAGS ?? []

    if (labFeatureFlags.length > 0) {
      dispatch(fetchLabFeatureFlagDecisions(
        labFeatureFlags,
        state.auth.resolvedTrackingId ?? state.auth.domainUserId ?? 'anonymous',
        true,
      ))
    }
  }
}

export function refreshLabRemoteConfigForCurrentUser(): AppAction {
  return (dispatch) => {
    const remoteConfigKeys = getLabRemoteConfigKeysForCurrentState()

    if (remoteConfigKeys.length > 0) {
      dispatch(fetchLabRemoteConfig(remoteConfigKeys, true))
    }
  }
}

export function fetchLabHeaderBanners(currentPath: string, distinctId: string, token?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const featureFlagContext = state.system.features.context
    const labGeoAttributes = getLabGeoAttributes(state)

    dispatch({
      type: API_CALL,
      api: FETCH_LAB_HEADER_BANNERS,
      requestPath: currentPath,
      request: () => getHeaderBanners({
        currentPath,
        distinctId,
        token,
        featureFlagContext,
        additionalAttributes: labGeoAttributes,
      }),
    })
  }
}

export function fetchLabModalPopups(currentPath: string, distinctId: string, token?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const featureFlagContext = state.system.features.context
    const labGeoAttributes = getLabGeoAttributes(state)
    const requestKey = getLabModalPopupsRequestKey({
      currentPath,
      distinctId,
      hasToken: Boolean(token),
      featureFlagContext,
      labGeoAttributes,
    })

    dispatch({
      type: API_CALL,
      api: FETCH_LAB_MODAL_POPUPS,
      requestPath: currentPath,
      requestKey,
      request: () => getModalPopups({
        currentPath,
        distinctId,
        token,
        featureFlagContext,
        additionalAttributes: labGeoAttributes,
      }),
    })
  }
}

/**
 * Refreshes funnel optimiser feature flags with current cart attributes and UTMs.
 * Returns the extracted funnel features for immediate use without needing to access store.
 *
 * UTMs are read from Redux state (populated by SSR from URL params on initial page load).
 */
export function refreshFunnelOptimiserFlags(cartAttributes: Record<string, unknown>): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const featureFlagContext = state.system.features.context
    const labGeoAttributes = getLabGeoAttributes(state)
    const distinctId = state.auth.domainUserId ?? 'anonymous'

    const utmAttributes: Record<string, string> = {}
    if (state.utm.campaign) utmAttributes.utmCampaign = state.utm.campaign
    if (state.utm.medium) utmAttributes.utmMedium = state.utm.medium
    if (state.utm.source) utmAttributes.utmSource = state.utm.source
    if (state.utm.term) utmAttributes.utmTerm = state.utm.term
    if (state.utm.content) utmAttributes.utmContent = state.utm.content

    try {
      const data = await getFeatureFlagDecisions(
        {
          keys: [LabFeatureFlags.enableFunnelOptimiser],
          distinctId,
          featureFlagContext,
          additionalAttributes: {
            ...utmAttributes,
            ...cartAttributes,
            ...(labGeoAttributes ?? {}),
          },
          forceRefresh: true,
          timeout: FUNNEL_OPTIMISER_API_TIMEOUT_MS,
        },
      )

      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_LAB_FEATURE_FLAG_DECISIONS,
        data,
      })

      const optimizelyExperiments = getState().system.features.experiments

      // Extract and return funnel features from the API response
      return extractFunnelFeatures(data?.[LabFeatureFlags.enableFunnelOptimiser], optimizelyExperiments)
    } catch(error) {
      reportClientError(error, { context: 'refreshFunnelOptimiserFlags' })
      return DEFAULT_FUNNEL_FEATURES
    }
  }
}

/**
 * Adds a historical action to the features state for later feature triggering.
 * The action will include a timestamp automatically.
 *
 * @param type - The type/name of the action (e.g., 'button_click', 'page_view', 'form_submit')
 * @param metadata - Optional metadata associated with the action
 * @returns An action that adds the historical action to the state
 */
export function addHistoricalAction(type: string, metadata?: Record<string, any>): AppAction {
  return {
    type: ADD_HISTORICAL_ACTION,
    action: {
      type,
      timestamp: new Date().toISOString(),
      metadata,
    },
  }
}

/**
 * Clears all historical actions from the features state.
 *
 * @returns An action that clears all historical actions
 */
export function clearHistoricalActions(): AppAction {
  return {
    type: CLEAR_HISTORICAL_ACTIONS,
  }
}

/**
 * Restores historical actions from sessionStorage.
 * This is used to hydrate the state when opening a new tab in the same session.
 *
 * @param actions - The historical actions to restore
 * @returns An action that restores the historical actions to the state
 */
export function restoreHistoricalActions(actions: Array<App.HistoricalAction>): AppAction {
  return {
    type: RESTORE_HISTORICAL_ACTIONS,
    actions,
  }
}
